import { prepareImageForUpload } from '../services/imagePrep.js';
import { parseLocalDate } from '../services/dates.js';
import { db } from '../services/firebase.js';
import { loadFirestore } from '../vendor.js';

export const ReceiptOCR = {
  name: "ReceiptOCR",
  inject: ["user", "isAuthenticated"],
  props: {
    autoSave: {
      type: Boolean,
      default: true
    },
    categoryOptions: {
      type: Array,
      default: () => [
        'Office Supplies',
        'Travel',
        'Meals & Entertainment',
        'Software',
        'Equipment',
        'Marketing',
        'Other'
      ]
    }
  },
  emits: ['expense-saved', 'expense-cancelled', 'text-processed'],
  data() {
    return {
      // Receipt capture state
      receiptCapture: {
        uploading: false,
        processing: false,
        progress: 0,
        imageUrl: null,
        imageFile: null,
        error: null
      },
      
      // Extracted text and parsed data
      extractedData: {
        merchant: "",
        amount: "",
        date: "",
        tax: "",
        rawText: "",
        confidence: {
          merchant: 0,
          amount: 0,
          date: 0
        }
      },
      
      // Expense form data
      expenseForm: {
        merchant: "",
        amount: "",
        date: "",
        category: "",
        description: "",
        tax: "",
        saving: false,
        errors: {
          merchant: "",
          amount: "",
          date: "",
          category: ""
        }
      },
      
      // UI state
      currentStep: 'capture', // 'capture', 'review', 'edit'
      editMode: false,
      showAdvancedFields: false,
      
      // Mobile detection
      isMobile: this.isMobileDevice(),
      
      // Processing states
      retryCount: 0,
      maxRetries: 3,
      
      // Preview modal
      previewModal: {
        show: false,
        imageUrl: "",
        title: "Receipt Preview"
      },
      
      // Text retrieval
      textListener: null,
      textTimeout: null
    };
  },
  
  computed: {
    hasImage() {
      return this.receiptCapture.imageUrl !== null;
    },
    
    hasExtractedData() {
      return this.extractedData.merchant || this.extractedData.amount || this.extractedData.date;
    },
    
    isProcessing() {
      return this.receiptCapture.uploading || this.receiptCapture.processing;
    },
    
    canProceedToReview() {
      return this.hasImage && this.hasExtractedData && !this.isProcessing;
    },
    
    formIsValid() {
      return this.expenseForm.merchant.trim() && 
             this.expenseForm.amount && 
             this.expenseForm.date &&
             this.expenseForm.category;
    },
    
    confidenceColor() {
      return (confidence) => {
        if (confidence >= 0.8) return 'text-success';
        if (confidence >= 0.6) return 'text-warning';
        return 'text-danger';
      };
    },
    
    totalAmount() {
      const amount = parseFloat(this.expenseForm.amount) || 0;
      const tax = parseFloat(this.expenseForm.tax) || 0;
      return (amount + tax).toFixed(2);
    }
  },
  
  methods: {
    // Image capture methods
    triggerImageCapture() {
      this.$refs.receiptInput.click();
    },
    
    async handleImageSelect(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Validate file
      if (!file.type.startsWith('image/')) {
        this.setError('Please select a valid image file.');
        return;
      }
      
      const maxSize = this.isMobile ? 20 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        const sizeLimit = this.isMobile ? '20MB' : '10MB';
        this.setError(`Image file size must be less than ${sizeLimit}.`);
        return;
      }
      
      this.clearError();
      
      // Process image
      if (this.isMobile) {
        setTimeout(() => {
          this.processReceiptImage(file);
        }, 100);
      } else {
        this.processReceiptImage(file);
      }
      
      // Clear input
      event.target.value = '';
    },
    
    async processReceiptImage(file) {
      this.receiptCapture.uploading = true;
      this.receiptCapture.progress = 0;
      
      try {
        // Process for mobile compatibility
        const processedFile = await this.processImageFile(file);
        
        // Create preview URL
        this.receiptCapture.imageUrl = URL.createObjectURL(processedFile);
        this.receiptCapture.imageFile = processedFile;
        
        // Upload to Firebase Storage
        const downloadURL = await this.uploadToFirebase(processedFile);
        
        // Update imageUrl with the Firebase URL
        this.receiptCapture.imageUrl = downloadURL;
        
        // Wait for text extraction and then retrieve it
        await this.waitForExtractedText();
        
      } catch (error) {
        console.error('Error processing receipt image:', error);
        this.setError('Error processing image. Please try again.');
        this.receiptCapture.uploading = false;
        this.receiptCapture.processing = false;
      } finally {
        this.receiptCapture.uploading = false;
      }
    },
    
    async uploadToFirebase(file) {
      // Check if Firebase is available
      if (typeof firebase === 'undefined' || !firebase.storage) {
        throw new Error('Firebase Storage is not initialized');
      }
      
      const timestamp = Date.now();
      // From the file being uploaded, not the original the user picked - the
      // two differ whenever a HEIC was converted.
      const fileExtension = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const fileName = `receipt_${this.user.uid}_${timestamp}.${fileExtension}`;
      const storagePath = `receipts/${this.user.uid}/${fileName}`;
      
      const storageRef = firebase.storage().ref(storagePath);
      const uploadTask = storageRef.put(file);
      
      return new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            this.receiptCapture.progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          },
          (error) => {
            console.error('Upload error:', error);
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
              resolve(downloadURL);
            } catch (error) {
              reject(error);
            }
          }
        );
      });
    },
    
    // Wait for text extraction and retrieve from Firestore
    async waitForExtractedText() {
      this.receiptCapture.processing = true;
      this.clearError();
      
      console.log('Waiting for text extraction for:', this.receiptCapture.imageUrl);
      
      try {
        await this.listenForExtractedText();
      } catch (error) {
        console.error('Text retrieval error:', error);
        
        if (this.retryCount < this.maxRetries) {
          this.retryCount++;
          this.setError(`Text extraction pending. Retrying... (${this.retryCount}/${this.maxRetries})`);
          setTimeout(() => this.waitForExtractedText(), 3000);
        } else {
          this.setError('Text extraction failed. Please enter details manually.');
          this.populateEmptyForm();
          this.currentStep = 'edit';
          this.receiptCapture.processing = false;
        }
      }
    },
    
    async listenForExtractedText() {
      console.log('Setting up text extraction listener...');
      
      // Get the image path that was uploaded
      const imagePath = this.getImagePathFromUrl(this.receiptCapture.imageUrl);
      console.log('Looking for extracted text for path:', imagePath);
      
      // Firestore is loaded on demand: it is ~0.8 MB of source and only this
      // listener reads from it (the expense-extracted-text collection written by
      // the storage-extract-image-text extension).
      await loadFirestore();
      if (!firebase.firestore) {
        throw new Error('Firestore failed to load.');
      }
      
      // Clean up any existing listener
      this.cleanupTextListener();
      
      // Listen for extracted text in Firestore
      const db = firebase.firestore();
      
      return new Promise((resolve, reject) => {
        // Set timeout for text extraction
        this.textTimeout = setTimeout(() => {
          console.log('Text extraction timeout reached');
          this.cleanupTextListener();
          reject(new Error('Text extraction timeout'));
        }, 45000); // 45 second timeout
        
        console.log('Starting Firestore listener for extracted text...');
        
        try {
          this.textListener = db.collection('expense-extracted-text')
            .onSnapshot((snapshot) => {
              console.log('Firestore snapshot received, changes:', snapshot.docChanges().length);
              
              snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                  const data = change.doc.data();
                  console.log('New document found:', change.doc.id, data);
                  
                  // Check if this document is for our uploaded file
                  const fileName = this.getFileNameFromPath(imagePath);
                  const docFileName = this.getFileNameFromPath(data.file || '');
                  
                  console.log('Comparing files:', fileName, 'vs', docFileName);
                  
                  if (fileName && docFileName && fileName === docFileName) {
                    console.log('Text extraction document matches our file!', data);
                    
                    // Check for text content
                    if (data.text) {
                      console.log('Text found, processing patterns...');
                      
                      // Clear timeout and cleanup
                      this.cleanupTextListener();
                      
                      // Process the extracted text for patterns
                      this.processExtractedText(data.text);
                      resolve();
                    } else {
                      console.log('Document found but no text content yet');
                    }
                  }
                }
              });
            }, (error) => {
              console.error('Firestore listener error:', error);
              this.cleanupTextListener();
              reject(error);
            });
        } catch (error) {
          console.error('Error setting up Firestore listener:', error);
          this.cleanupTextListener();
          reject(error);
        }
      });
    },
    
    cleanupTextListener() {
      if (this.textTimeout) {
        clearTimeout(this.textTimeout);
        this.textTimeout = null;
      }
      
      if (this.textListener) {
        this.textListener();
        this.textListener = null;
      }
    },
    
    getFileNameFromPath(path) {
      if (!path) return null;
      return path.split('/').pop();
    },
    
    processExtractedText(rawText) {
      try {
        if (!rawText) {
          throw new Error('No text found in extraction results');
        }
        
        console.log('Raw extracted text:', rawText);
        
        // Parse the text for meaningful patterns
        const parsed = this.parseReceiptText(rawText);
        
        this.extractedData = {
          merchant: parsed.merchant || "",
          amount: parsed.amount || "",
          date: parsed.date || "",
          tax: parsed.tax || "",
          rawText: rawText,
          confidence: parsed.confidence || { merchant: 0, amount: 0, date: 0 }
        };
        
        // Populate form with extracted data
        this.populateFormFromExtractedData();
        
        // Move to review step
        this.currentStep = 'review';
        this.receiptCapture.processing = false;
        
        this.$emit('text-processed', { 
          extractedData: this.extractedData, 
          formData: this.expenseForm 
        });
        
      } catch (error) {
        console.error('Error processing extracted text:', error);
        this.setError('Error processing extracted text');
        this.receiptCapture.processing = false;
        // Fallback to edit mode if processing fails
        this.populateEmptyForm();
        this.currentStep = 'edit';
      }
    },
    
    getImagePathFromUrl(url) {
      try {
        if (!url) return null;
        
        // Handle blob URLs
        if (url.startsWith('blob:')) {
          return null;
        }
        
        // Handle Firebase Storage URLs
        const urlParts = url.split('/o/')[1];
        if (!urlParts) return null;
        
        const pathWithParams = urlParts.split('?')[0];
        return decodeURIComponent(pathWithParams);
      } catch (error) {
        console.error('Error extracting path from URL:', error);
        return null;
      }
    },
    
    // Parse receipt text for meaningful patterns
    parseReceiptText(text) {
      const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
      
      const result = {
        merchant: '',
        amount: '',
        date: '',
        tax: '',
        confidence: {
          merchant: 0,
          amount: 0,
          date: 0
        }
      };
      
      // Find total amount
      const totalData = this.findTotal(lines);
      result.amount = totalData.amount;
      result.confidence.amount = totalData.confidence;
      
      // Find tax amount
      const taxData = this.findTax(lines);
      result.tax = taxData.amount;
      
      // Find merchant name
      const merchantData = this.findMerchant(lines);
      result.merchant = merchantData.name;
      result.confidence.merchant = merchantData.confidence;
      
      // Find date
      const dateData = this.findDate(lines);
      result.date = dateData.date;
      result.confidence.date = dateData.confidence;
      
      return result;
    },
    
    findTotal(lines) {
      // Enhanced patterns for finding "total with a value"
      const totalPatterns = [
        // Exact "TOTAL" with amount patterns (highest priority)
        /^total[\s:]+\$?(\d+\.?\d{2})$/i,
        /^total[\s:]*\$(\d+\.?\d{2})$/i,
        /^total[\s:]*(\d+\.\d{2})$/i,
        
        // "TOTAL" with descriptive text and amount
        /total[\s:]+.*?(\d+\.\d{2})$/i,
        /total[\s:]+[^0-9]*(\d+\.\d{2})$/i,
        
        // Other high-confidence total indicators
        /^grand total[\s:]*\$?(\d+\.?\d{2})$/i,
        /^amount due[\s:]*\$?(\d+\.?\d{2})$/i,
        /^balance due[\s:]*\$?(\d+\.?\d{2})$/i,
        /^total amount[\s:]*\$?(\d+\.?\d{2})$/i,
        
        // Medium confidence patterns
        /^subtotal[\s:]*\$?(\d+\.?\d{2})$/i,
        /^amount[\s:]*\$?(\d+\.?\d{2})$/i,
        /^balance[\s:]*\$?(\d+\.?\d{2})$/i,
        
        // Lower confidence - any line containing "total" and an amount
        /.*total.*(\d+\.\d{2})/i,
        
        // Fallback - standalone amounts with proper formatting
        /^[\s]*\$?(\d+\.\d{2})[\s]*$/
      ];
      
      let bestMatch = { amount: '', confidence: 0 };
      
      console.log('Analyzing lines for total amounts:', lines);
      
      // Look for explicit "TOTAL" mentions first (highest confidence)
      for (const line of lines) {
        const cleanLine = line.trim();
        
        for (let i = 0; i < totalPatterns.length; i++) {
          const match = cleanLine.match(totalPatterns[i]);
          if (match) {
            const amountStr = match[1];
            const amount = parseFloat(amountStr);
            
            console.log(`Pattern ${i} matched "${cleanLine}" -> amount: ${amount}`);
            
            if (!isNaN(amount) && amount > 0 && amount < 50000) { // reasonable receipt range
              // Calculate confidence based on pattern specificity
              let confidence = 0;
              
              if (i <= 2) {
                // Exact "TOTAL" patterns
                confidence = 0.95;
              } else if (i <= 4) {
                // "TOTAL" with descriptive text
                confidence = 0.90;
              } else if (i <= 8) {
                // Other high-confidence indicators
                confidence = 0.85;
              } else if (i <= 11) {
                // Medium confidence patterns
                confidence = 0.75;
              } else if (i === 12) {
                // Any line with "total"
                confidence = 0.60;
              } else {
                // Standalone amounts
                confidence = 0.30;
              }
              
              // Boost confidence for properly formatted amounts (X.XX)
              if (/^\d+\.\d{2}$/.test(amountStr)) {
                confidence += 0.05;
              }
              
              // Boost confidence if line starts with "total"
              if (/^total/i.test(cleanLine)) {
                confidence += 0.05;
              }
              
              // Reduce confidence for very small amounts (likely not totals)
              if (amount < 1.00) {
                confidence *= 0.5;
              }
              
              console.log(`Confidence for "${cleanLine}": ${confidence}`);
              
              if (confidence > bestMatch.confidence) {
                bestMatch = {
                  amount: amount.toFixed(2),
                  confidence: confidence
                };
                console.log(`New best match: ${amount.toFixed(2)} with confidence ${confidence}`);
              }
            }
          }
        }
      }
      
      // If no good total found, analyze all amounts and pick the most likely candidate
      if (bestMatch.confidence < 0.5) {
        console.log('No high-confidence total found, analyzing all amounts...');
        
        const amounts = [];
        const amountPattern = /\$?(\d+\.\d{2})/g;
        
        for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
          const line = lines[lineIndex];
          let match;
          
          while ((match = amountPattern.exec(line)) !== null) {
            const amountStr = match[1];
            const amount = parseFloat(amountStr);
            
            if (!isNaN(amount) && amount > 0 && amount < 50000) {
              amounts.push({
                amount: amount,
                line: line,
                lineIndex: lineIndex,
                position: match.index
              });
            }
          }
          amountPattern.lastIndex = 0; // Reset regex
        }
        
        if (amounts.length > 0) {
          // Sort amounts by value (descending) and analyze context
          amounts.sort((a, b) => b.amount - a.amount);
          
          console.log('All amounts found:', amounts);
          
          // Look for the largest amount that appears to be a total
          for (const amountInfo of amounts) {
            let confidence = 0.25; // Base confidence for largest amount
            
            // Boost confidence based on position (totals usually appear later)
            const positionRatio = amountInfo.lineIndex / lines.length;
            if (positionRatio > 0.7) confidence += 0.15; // Near end of receipt
            else if (positionRatio > 0.5) confidence += 0.10; // Middle to end
            
            // Boost confidence if it's the largest amount
            if (amountInfo.amount === amounts[0].amount) {
              confidence += 0.10;
            }
            
            // Check surrounding context for total-like words
            const contextWords = ['total', 'amount', 'due', 'balance', 'charge'];
            const lineLower = amountInfo.line.toLowerCase();
            
            for (const word of contextWords) {
              if (lineLower.includes(word)) {
                confidence += 0.20;
                break;
              }
            }
            
            console.log(`Amount ${amountInfo.amount} context confidence: ${confidence}`);
            
            if (confidence > bestMatch.confidence) {
              bestMatch = {
                amount: amountInfo.amount.toFixed(2),
                confidence: confidence
              };
            }
          }
        }
      }
      
      console.log('Final total match:', bestMatch);
      return bestMatch;
    },
    
    findTax(lines) {
      const taxPatterns = [
        /^tax[\s:]*\$?(\d+\.?\d*)$/i,
        /^sales tax[\s:]*\$?(\d+\.?\d*)$/i,
        /^hst[\s:]*\$?(\d+\.?\d*)$/i,
        /^gst[\s:]*\$?(\d+\.?\d*)$/i,
        /tax.*?(\d+\.\d{2})/i
      ];
      
      for (const line of lines) {
        for (const pattern of taxPatterns) {
          const match = line.match(pattern);
          if (match) {
            const amount = parseFloat(match[1]);
            if (!isNaN(amount) && amount >= 0) {
              return { amount: amount.toFixed(2) };
            }
          }
        }
      }
      
      return { amount: '' };
    },
    
    findMerchant(lines) {
      // Merchant is usually in the first few lines
      const skipPatterns = [
        /^\d+\.\d{2}$/, // pure amounts
        /^\$\d+/, // dollar amounts
        /\d{1,4}\/\d{1,4}\/\d{2,4}/, // dates
        /^\d+\s+\w+\s+(st|street|ave|avenue|rd|road|blvd|boulevard|dr|drive|way|lane|ln|ct|court)/i, // addresses
        /^(phone|tel|fax)[\s:]/i, // contact info
        /^(receipt|invoice)[\s#]/i, // receipt headers
        /^total[\s:]/i, // totals
        /^tax[\s:]/i, // tax lines
        /^\d{3}-\d{3}-\d{4}$/, // phone numbers
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/ // emails
      ];
      
      // Look at first 5 lines for merchant name
      for (let i = 0; i < Math.min(5, lines.length); i++) {
        const line = lines[i];
        
        // Skip if matches any skip pattern
        if (skipPatterns.some(pattern => pattern.test(line))) {
          continue;
        }
        
        // Must be reasonable length and contain letters
        if (line.length >= 3 && line.length <= 50 && /[a-zA-Z]/.test(line)) {
          return {
            name: line.trim(),
            confidence: i === 0 ? 0.9 : Math.max(0.8 - (i * 0.1), 0.5)
          };
        }
      }
      
      return { name: '', confidence: 0 };
    },
    
    findDate(lines) {
      const datePatterns = [
        /(\d{1,2}\/\d{1,2}\/\d{2,4})/,
        /(\d{1,2}-\d{1,2}-\d{2,4})/,
        /(\d{4}-\d{1,2}-\d{1,2})/,
        /(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s+\d{1,2},?\s+\d{2,4}/i,
        /(\d{1,2}\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s+\d{2,4})/i
      ];
      
      for (const line of lines) {
        for (const pattern of datePatterns) {
          const match = line.match(pattern);
          if (match) {
            const dateStr = match[1] || match[0];
            const parsedDate = this.parseDateString(dateStr);
            
            if (parsedDate) {
              return {
                date: parsedDate,
                confidence: 0.8
              };
            }
          }
        }
      }
      
      return { date: '', confidence: 0 };
    },
    
    parseDateString(dateStr) {
      // Try to parse various date formats and return YYYY-MM-DD
      const date = new Date(dateStr);
      
      if (!isNaN(date.getTime())) {
        // Check if date is reasonable (not too far in past/future)
        const now = new Date();
        const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        const oneMonthForward = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
        
        if (date >= oneYearAgo && date <= oneMonthForward) {
          return date.toISOString().split('T')[0]; // YYYY-MM-DD format
        }
      }
      
      return null;
    },
    
    populateFormFromExtractedData() {
      this.expenseForm.merchant = this.extractedData.merchant;
      this.expenseForm.amount = this.parseAmount(this.extractedData.amount);
      this.expenseForm.date = this.parseExpenseDate(this.extractedData.date);
      this.expenseForm.tax = this.parseAmount(this.extractedData.tax);
      this.expenseForm.description = `Receipt from ${this.extractedData.merchant}`.trim();
      
      // Auto-suggest category based on merchant
      this.expenseForm.category = this.suggestCategory(this.extractedData.merchant);
    },
    
    populateEmptyForm() {
      const today = new Date().toISOString().split('T')[0];
      this.expenseForm = {
        ...this.expenseForm,
        merchant: "",
        amount: "",
        date: today,
        category: "",
        description: "",
        tax: ""
      };
    },
    
    // Form editing methods
    enterEditMode() {
      this.editMode = true;
      this.currentStep = 'edit';
      this.clearFormErrors();
    },
    
    exitEditMode() {
      this.editMode = false;
      this.currentStep = 'review';
    },
    
    // Validation methods
    validateForm() {
      this.clearFormErrors();
      let isValid = true;
      
      if (!this.expenseForm.merchant.trim()) {
        this.expenseForm.errors.merchant = "Merchant name is required";
        isValid = false;
      }
      
      if (!this.expenseForm.amount || parseFloat(this.expenseForm.amount) <= 0) {
        this.expenseForm.errors.amount = "Valid amount is required";
        isValid = false;
      }
      
      if (!this.expenseForm.date) {
        this.expenseForm.errors.date = "Date is required";
        isValid = false;
      }
      
      if (!this.expenseForm.category) {
        this.expenseForm.errors.category = "Category is required";
        isValid = false;
      }
      
      return isValid;
    },
    
    clearFormErrors() {
      this.expenseForm.errors = {
        merchant: "",
        amount: "",
        date: "",
        category: ""
      };
    },
    
    // Save methods
    async saveExpense() {
      if (!this.validateForm()) {
        return;
      }
      
      this.expenseForm.saving = true;
      
      try {
        const expenseData = {
          merchant: this.expenseForm.merchant.trim(),
          amount: parseFloat(this.expenseForm.amount),
          date: this.expenseForm.date,
          category: this.expenseForm.category,
          description: this.expenseForm.description.trim(),
          tax: parseFloat(this.expenseForm.tax) || 0,
          receiptImageUrl: this.receiptCapture.imageUrl,
          extractedData: this.extractedData,
          userId: this.user.uid,
          createdAt: firebase.database.ServerValue.TIMESTAMP,
          updatedAt: firebase.database.ServerValue.TIMESTAMP
        };
        
        // Save to Firebase
        const expenseRef = await firebase.database().ref('expenses').push(expenseData);
        
        this.$emit('expense-saved', {
          id: expenseRef.key,
          ...expenseData
        });
        
        // Reset component
        this.resetComponent();
        
      } catch (error) {
        console.error('Error saving expense:', error);
        this.setError('Error saving expense. Please try again.');
      } finally {
        this.expenseForm.saving = false;
      }
    },
    
    cancelExpense() {
      this.$emit('expense-cancelled');
      this.resetComponent();
    },
    
    // Utility methods
    parseAmount(amountStr) {
      if (!amountStr) return "";
      const cleaned = amountStr.replace(/[$,\s]/g, '');
      const amount = parseFloat(cleaned);
      return isNaN(amount) ? "" : amount.toFixed(2);
    },
    
    parseExpenseDate(dateStr) {
      if (!dateStr) return new Date().toISOString().split('T')[0];
      
      // Try to parse various date formats
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
      
      return new Date().toISOString().split('T')[0];
    },
    
    suggestCategory(merchant) {
      if (!merchant) return "";
      
      const merchantLower = merchant.toLowerCase();
      
      // Simple category suggestions
      if (merchantLower.includes('restaurant') || merchantLower.includes('cafe') || 
          merchantLower.includes('pizza') || merchantLower.includes('bar')) {
        return 'Meals & Entertainment';
      }
      if (merchantLower.includes('gas') || merchantLower.includes('fuel') || 
          merchantLower.includes('airport') || merchantLower.includes('hotel')) {
        return 'Travel';
      }
      if (merchantLower.includes('office') || merchantLower.includes('supply') || 
          merchantLower.includes('depot')) {
        return 'Office Supplies';
      }
      if (merchantLower.includes('software') || merchantLower.includes('app') || 
          merchantLower.includes('subscription')) {
        return 'Software';
      }
      
      return "";
    },
    
    formatCurrency(value) {
      const num = parseFloat(value) || 0;
      return num.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      });
    },
    
    formatDate(dateStr) {
      const date = parseLocalDate(dateStr);
      if (!date) return "";
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },
    
    // Image processing
    // Receipts must reach Cloud Vision as something it can decode. The old
    // version only converted on mobile and only above 2 MB, so a desktop upload
    // or a small iPhone photo went up as raw HEIC - which Vision accepts, finds
    // no text in, and reports as a success.
    async processImageFile(file) {
      return prepareImageForUpload(file, { maxWidth: 2400, maxHeight: 2400, quality: 0.92 });
    },
    
    resizeImage(file, maxWidth, maxHeight) {
      return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
          let { width, height } = img;
          
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width *= ratio;
            height *= ratio;
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              });
              resolve(resizedFile);
            } else {
              reject(new Error('Failed to resize image'));
            }
          }, file.type, 0.9);
        };
        
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
    },
    
    isMobileDevice() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
             (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
    },
    
    // UI helper methods
    setError(message) {
      this.receiptCapture.error = message;
    },
    
    clearError() {
      this.receiptCapture.error = null;
    },
    
    resetComponent() {
      // Clean up listeners and timeouts
      this.cleanupTextListener();
      
      // Clean up image URL
      if (this.receiptCapture.imageUrl && this.receiptCapture.imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.receiptCapture.imageUrl);
      }
      
      this.receiptCapture = {
        uploading: false,
        processing: false,
        progress: 0,
        imageUrl: null,
        imageFile: null,
        error: null
      };
      
      this.extractedData = {
        merchant: "",
        amount: "",
        date: "",
        tax: "",
        rawText: "",
        confidence: { merchant: 0, amount: 0, date: 0 }
      };
      
      this.expenseForm = {
        merchant: "",
        amount: "",
        date: "",
        category: "",
        description: "",
        tax: "",
        saving: false,
        errors: { merchant: "", amount: "", date: "", category: "" }
      };
      
      this.currentStep = 'capture';
      this.editMode = false;
      this.retryCount = 0;
    },
    
    openPreviewModal() {
      this.previewModal.imageUrl = this.receiptCapture.imageUrl;
      this.previewModal.show = true;
      
      // Check if Bootstrap is available
      if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
        const modal = new bootstrap.Modal(document.getElementById('receiptPreviewModal'));
        modal.show();
      } else {
        // Fallback for when Bootstrap JS isn't available
        const modalElement = document.getElementById('receiptPreviewModal');
        if (modalElement) {
          modalElement.style.display = 'block';
          modalElement.classList.add('show');
        }
      }
    },
    
    closePreviewModal() {
      this.previewModal.show = false;
      const modalElement = document.getElementById('receiptPreviewModal');
      if (modalElement) {
        modalElement.style.display = 'none';
        modalElement.classList.remove('show');
      }
    },
    
    retakePhoto() {
      this.resetComponent();
    }
  },
  
  mounted() {
    this.isMobile = this.isMobileDevice();
    
    // Add debugging for Firebase availability
    console.log('Firebase available:', typeof firebase !== 'undefined');
    if (typeof firebase !== 'undefined') {
      console.log('Firebase Storage available:', !!firebase.storage);
      console.log('Firebase Firestore available:', !!firebase.firestore);
    }
  },
  
  beforeUnmount() {
    // Clean up listeners and timeouts
    this.cleanupTextListener();
    
    // Clean up blob URLs
    if (this.receiptCapture.imageUrl && this.receiptCapture.imageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.receiptCapture.imageUrl);
    }
  },
  
  template: '#expense-capture'
};
